/**
 * AISidebar — adapter that exposes the ported Holly widget under the legacy
 * `AISidebar` prop contract so existing consumers (Registration, Transport)
 * change only their import path.
 *
 * Two modes:
 *  - Controlled (registration): `controlledOpen` is defined → the parent owns
 *    open/close; `initialMessage` is seeded silently on open; a register CTA
 *    fires `onReadyToRegister(threadId)` for the in-page handoff. Welcome chips
 *    are suppressed.
 *  - Uncontrolled (/embed Transport): no `controlledOpen` → a hidden
 *    `[data-sidebar]` host + MutationObserver lets `AIInput` open + seed the
 *    chat via `data-open`/`data-message` (the legacy trigger mechanism).
 */

import { useEffect, useRef } from 'react';
import { MAPBOX_APIKEY } from '@constants';
import { HollyProvider, useHolly } from './HollyContext';
import HollyChatPanel from './HollyChatPanel';
import './holly.css';

export type DeclaredIntent =
  | 'warehouse_search'
  | 'transport_booking'
  | 'lsp_listing';

export interface AISidebarProps {
  /** When set, the parent owns the open/closed state (registration). */
  controlledOpen?: boolean;
  /** Fires when the user closes the drawer (× / backdrop / Escape). */
  onClose?: () => void;
  /**
   * Sent on the first orchestrate call so Holly enters declared-intent mode.
   * NOTE: the gateway currently drops this — intent is carried by
   * `initialMessage` free text. Kept for contract parity / forward-compat.
   */
  declaredIntent?: DeclaredIntent;
  /** Seed message auto-fired silently on first open (no user bubble). */
  initialMessage?: string;
  /**
   * Fired when the user clicks Holly's register CTA. The parent receives the
   * threadId so the registration screen can hydrate from the Holly session.
   */
  onReadyToRegister?: (threadId: string) => void;
}

/* Inner component — lives inside HollyProvider so it can use the context for
 * the silent seed (controlled) and the data-sidebar observer (uncontrolled). */
function HollyAdapterInner({
  isControlled,
  initialMessage,
}: {
  isControlled: boolean;
  initialMessage?: string;
}) {
  const { isChatOpen, openChat, seedMessage, messages, threadId } = useHolly();
  const seededRef = useRef(false);

  /* Controlled mode: silently seed `initialMessage` once, when the panel is
   * open and the conversation is still empty. */
  useEffect(() => {
    if (!isControlled || !initialMessage) return;
    if (!isChatOpen) return;
    if (seededRef.current) return;
    if (messages.length > 0 || threadId) return;
    seededRef.current = true;
    seedMessage(initialMessage);
  }, [
    isControlled,
    initialMessage,
    isChatOpen,
    messages.length,
    threadId,
    seedMessage,
  ]);

  /* Uncontrolled mode: observe the `[data-sidebar]` host so AIInput can open +
   * seed the chat (sets data-open=true and data-message=<directive>). */
  useEffect(() => {
    if (isControlled) return undefined;
    const el = document.querySelector('[data-sidebar]');
    if (!el) return undefined;

    const handle = () => {
      const isOpen = el.getAttribute('data-open') === 'true';
      const message = el.getAttribute('data-message');
      if (isOpen) openChat();
      if (isOpen && message) {
        /* Clear so the same directive doesn't refire on later mutations. */
        el.setAttribute('data-message', '');
        seedMessage(message);
      }
    };

    const observer = new MutationObserver(handle);
    observer.observe(el, {
      attributes: true,
      attributeFilter: ['data-open', 'data-message'],
    });
    return () => observer.disconnect();
  }, [isControlled, openChat, seedMessage]);

  return (
    <>
      {!isControlled && (
        <div data-sidebar data-open='false' data-message='' hidden />
      )}
      <HollyChatPanel />
    </>
  );
}

export function AISidebar({
  controlledOpen,
  onClose,
  declaredIntent,
  initialMessage,
  onReadyToRegister,
}: AISidebarProps = {}) {
  const isControlled = controlledOpen !== undefined;

  return (
    <HollyProvider
      mapboxToken={MAPBOX_APIKEY}
      controlledOpen={controlledOpen}
      onRequestClose={onClose}
      onReadyToRegister={onReadyToRegister}
      declaredIntent={declaredIntent ?? null}
      enableWelcomeChips={!isControlled}
    >
      <HollyAdapterInner
        isControlled={isControlled}
        initialMessage={initialMessage}
      />
    </HollyProvider>
  );
}
